<template>
	<svg
		v-if="iconsLoaded"
		:viewBox="getViewBox"
		:style="getSize"
		:class="[aspectRatio]"
		class="timpex-icon"
	>
		<title
			v-if="title"
			v-text="title"
		/>
		<use
			v-if="type == 'bp'"
			:class="[color]"
			:xlink:href="getBlueprintHref"
			class="icon-outline"
		/>

		<template v-else>
			<use :class="[color]" :xlink:href="getFillHref" class="icon-fill"/>
			<use :class="[color]" :xlink:href="getOutlineHref" class="icon-outline"/>
		</template>

	</svg>
</template>

<script>
import iconMixin from './iconMixin';
import Vue from 'vue';

let store = Vue.observable({ iconsLoaded: false, }); // no need to mount the whole instance, as this is what data uses under the hood, observable is meant for these cases.

(async function renderIcons() {
	if ( store.iconsLoaded ) { return; }
	store.iconsLoaded = true;

	let ticon = await import('!!raw-loader!@/assets/icons/timpexIcons.svg');
	let bicon = await import('!!raw-loader!@/assets/icons/bpi-16.svg');

	let fragment = document.createDocumentFragment();
	let ticonContainer = document.createElement('template');
	let biconContainer = document.createElement('template');

	ticonContainer.innerHTML = ticon.default;
	biconContainer.innerHTML = bicon.default;

	fragment.appendChild(biconContainer.content);
	fragment.appendChild(ticonContainer.content);

	document.body.appendChild(fragment);
}());

export default {
	name: 'TTimpexIcon',
	mixins: [iconMixin], // maybe consideer inheritAttr false, and allowing a case on svg attrs ?
	inheritAttrs: false,
	props: {
		/**
		 * The icon-name.
		 */
		icon: {
			type: String,
			required: true,
		},
		/**
		 * The color of the icon. Available values: `neutral, blue, green, orange, red, white`. Default is inherited from parent.
		 */
		color: String,
		/**
		 * The type of the icon. Available values: `bp` (which is BlueprintIcons).
		 */
		type: {
			type: String,
			default: ''
		},
		/**
		 * The title/tooltip of the icon.
		 */
		title: {
			type: String
		},
		aspectRatio: {
			type: String,
			default: 'auto',
			validator: function(value) {
				return [ 'auto', 'square' ].indexOf(value) !== -1;
			}
		}
	},
	computed: {
		iconsLoaded() {
			return store.iconsLoaded;
		},
		getOutlineHref() {
			return `#${this.icon}-outline`;
		},
		getFillHref() {
			return `#${this.icon}-fill`;
		},
		getBlueprintHref() {
			return `#${this.icon}`;
		},
		getViewBox() {
			if (this.type === 'bp') return '0 0 16 16';
			switch (this.icon) {
			case 'van':
				return '0 0 38 32';
			case 'tractor':
			case 'trailer':
			case 'crane':
			case 'truck':
			case 'train':
				return '0 0 48 32';
			case 'plane':
				return '0 0 64 32';
			case 'ferry':
				return '0 0 72 32';
			case 'semi':
				return '0 0 93 32';
			default:
				return '0 0 32 32';
			}
		}
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
// returns: eg. .blue { color: #10caf4}
@mixin colors($property: 'color', $type: 'base') {
	@each $color, $value in $palette {
		&.#{$color} {
			#{$property}: get-palette-color($color, $type);
		}
	}
}

.timpex-icon {
	height: 1em;
	&.square {
		width: 1em;
		min-width: 1em;
	}
}

.icon-fill {
	color: transparent;
	fill: currentColor;
	@include colors('color', 'light');
}

.icon-outline {
	color: inherit;
	fill: currentColor;
	@include colors('color', 'base');
	&.yellow {
		color: get-color($yellow, 'dark');
	}
}
</style>

<docs>
```html
<t-timpex-icon icon="trailer" color="blue"/>
```

```jsx noeditor
new Vue({
	data(){
		return {
			icons: ['semi', 'van', 'truck', 'crane', 'trailer', 'tractor', 'ferry', 'train', 'plane', 'order', 'order-new', 'order-pallet',
				'order-fish', 'order-cold', 'order-frozen', 'order-explosive', 'order-gas', 'order-heated', 'order-warning', 'orders', 'pickup',
				'delivery', 'pickup-delivery', 'trip', 'trip-new', 'truck-front', 'trip-end',
			],
			colors: ['none', 'neutral', 'blue', 'green', 'orange', 'red', 'white'],
			selectedColor: 'blue',
		}
	},
	computed: {
		isWhite() {
			return this.selectedColor == 'white';
		},
		background() {
			return this.isWhite ? 'overlay' : 'tint1';
		},
		color() {
			return this.isWhite ? {color: 'white'} : undefined;
		}
	},
	template: `
		<span>
			<t-select :items="colors" width="150px" :selectedItem="selectedColor" @change="selectedColor = $event" label="Color" />
			<div style="display: grid; margin-top: 10px; grid-row-gap: 6px; grid-column-gap: 4px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));">
				<t-pane v-for="icon in icons" :key="icon" height="100px" style="flex-direction: column; width: 150px;" :background="background" center>
					<t-timpex-icon :icon="icon" :color="selectedColor"/>
					<span style="font-size: 12px;" :style="color">{{icon}}</span>
				</t-pane>
			</div>
		</span>
	`
});

```

#### Size
```html
<t-timpex-icon icon="trailer" color="neutral" size="small" />
```

```jsx noeditor
<div style="display: grid; grid-row-gap: 6px; grid-column-gap: 4px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));">
	<t-pane style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
		<t-timpex-icon icon="trailer" color="neutral" size="xx-small"/>
		<span>xx-small</span>
	</t-pane>
	<t-pane style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
		<t-timpex-icon icon="trailer" color="neutral" size="x-small"/>
		<span>x-small</span>
	</t-pane>
	<t-pane style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
		<t-timpex-icon icon="trailer" color="neutral" size="small"/>
		<span>small</span>
	</t-pane>
	<t-pane style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
		<t-timpex-icon icon="trailer" color="neutral" size="medium"/>
		<span>medium</span>
	</t-pane>
	<t-pane style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
		<t-timpex-icon icon="trailer" color="neutral" size="large"/>
		<span>large</span>
	</t-pane>
</div>
```


#### Blueprint Icons

```html
<t-timpex-icon type="bp" icon="globe" color="neutral"/>
```

```jsx noeditor
new Vue({
	data(){
		return {
			icons: [
				'comparison','globe', 'menu-closed', 'menu-open', 'search', 'cross', 'more', 'plus', 'social-media', 'flows', 'map-marker',
				'polygon-filter', 'select', 'draw', 'layers', 'tick', 'person', 'phone', 'home', 'envelope', 'history', 'trash', 'import',
				'unknown-vehicle', 'swap-vertical', 'application', 'annotation', 'add', 'lock', 'unlock', 'drag-handle-vertical', 'filter',
				'filter-keep', 'filter-list', 'calendar', 'geosearch', 'maximize', 'minimize', 'caret-down', 'settings', 'user', 'dollar',
				'th-derived', 'search-text', 'arrow-up', 'arrow-down', 'cog', 'box', 'minus', 'list', 'label', 'id-number', 'tag', 'new-link',
				'edit', 'numbered-list', 'database', 'refresh', 'play', 'stop', 'collapse-all', 'expand-all', 'add-to-artifact', 'one-column',
				'two-columns', 'symbol-square', 'time', 'locate', 'warning-sign', 'info-sign', 'random', 'edit', 'numbered-list', 'database',
				'refresh', 'office', 'diagram-tree', 'disable', 'blocked-person', 'filter-open', 'filter-remove', 'mobile-phone', 'export',
				'arrow-left', 'new-person', 'error', 'flow-linear', 'inheritance', 'media', 'signature', 'chevron-left', 'chevron-right',
				'chevron-up', 'chevron-down', 'book', 'comment', 'document', 'document-open', 'duplicate', 'chat', 'eye-open', 'eye-off',
				'dot','help', 'waybill', 'package-label', 'edi',
			],
			search: '',
		}
	},

	computed: {
		filteredIcons() {
			return this.icons.filter(icon => icon.includes(this.search));
		}
	},
	methods: {
		onInput(value) {
			this.search = value;
		}
	},
	template: `
	<div>
		<t-search-input placeholder="Search for icon eg. 'globe'" :value="search" @input="onInput" width="100%"/>
		<div style="display: grid; grid-row-gap: 6px; grid-column-gap: 4px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); margin-top: 10px;">
			<t-pane v-for="icon in filteredIcons" :key="icon" style="flex-direction: column; width: 150px;" height="100px" background="tint1" center>
				<t-timpex-icon type="bp" :icon="icon" color="neutral"/>
				<span style="font-size: 12px;">{{icon}}</span>
			</t-pane>
		</div>
	</div>
	`
});
```

</docs>
